import type { BuildingAnalytics } from "@/lib/api/building";
import type { WidgetSpec } from "@/lib/api/agent";

export type ReportScopeLevel = "building" | "group" | "inventory";

export type ReportTemplateId = "energy" | "conditions" | "compare" | "history";

export interface ReportTemplate {
  id: ReportTemplateId;
  label: string;
}

export function templates_for_scope(level: ReportScopeLevel): ReportTemplate[] {
  if (level === "building") {
    return [
      { id: "energy", label: "Energiaraportti" },
      { id: "conditions", label: "Olosuhteet — Lämpötila & CO₂" },
      { id: "history", label: "Toimintahistoria" },
    ];
  }
  return [
    { id: "energy", label: "Energia-analyysi" },
    { id: "compare", label: "Kiinteistövertailu" },
    { id: "history", label: "Toimintahistoria" },
  ];
}

export function energy_kpi_items(
  snapshots: Pick<
    BuildingAnalytics,
    "total_energy_kwh" | "average_temperature" | "average_occupancy"
  >[],
): WidgetSpec & { type: "render_kpi_summary" } {
  const n = snapshots.length || 1;
  const total_energy = snapshots.reduce(
    (sum, snap) => sum + snap.total_energy_kwh,
    0,
  );
  const avg_temp =
    snapshots.reduce((sum, snap) => sum + snap.average_temperature, 0) / n;
  const avg_occ =
    snapshots.reduce((sum, snap) => sum + snap.average_occupancy, 0) / n;
  return {
    type: "render_kpi_summary",
    title: "Energia",
    items: [
      { label: "Energia", value: total_energy, unit: "kWh" },
      { label: "Lämpötila", value: avg_temp, unit: "°C" },
      { label: "Käyttöaste", value: avg_occ, unit: "%" },
    ],
  };
}

export function widgets_for_template(opts: {
  template_id: ReportTemplateId;
  level: ReportScopeLevel;
  building_id: string | null;
  building_ids: string[];
  days: 7 | 30 | 90;
  kpi?: WidgetSpec & { type: "render_kpi_summary" };
}): WidgetSpec[] {
  const { template_id, building_id, building_ids, days, kpi } = opts;

  if (template_id === "history") return [];

  if (template_id === "conditions" && building_id) {
    return [
      {
        type: "render_trend_chart",
        building_id,
        channels: ["indoor_temperature", "co2_ppm"],
        days,
        title: "Lämpötila & CO₂",
      },
    ];
  }

  if (template_id === "compare") {
    return [
      {
        type: "render_building_comparison",
        metric: "optimization_potential_eur_month",
        building_ids,
      },
    ];
  }

  if (template_id === "energy" && building_id && opts.level === "building") {
    return [
      ...(kpi ? [kpi] : []),
      {
        type: "render_trend_chart",
        building_id,
        channels: ["energy_kwh"],
        days,
        title: "Energia",
      },
    ];
  }

  if (template_id === "energy") {
    return [
      ...(kpi ? [kpi] : []),
      {
        type: "render_multi_building_trend",
        building_ids,
        metric: "energy_kwh",
        days,
        title: "Energia",
      },
    ];
  }

  return [];
}
